import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { api, API_BASE } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Fingerprint, ArrowLeft, CalendarCheck, Clock, MapPin, Loader2, XCircle,
  CalendarClock, CheckCircle2, CalendarPlus, Phone, SearchX,
} from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "../data/content";

export default function ManageAppointment() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [justCanceled, setJustCanceled] = useState(false);
  const [justRescheduled, setJustRescheduled] = useState(false);
  // Cancel
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  // Reschedule
  const [reschedOpen, setReschedOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [closedReason, setClosedReason] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/manage/${token}`);
        setAppointment(res.data.appointment);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const fetchSlots = useCallback(async (d) => {
    setSlotsLoading(true);
    setClosedReason("");
    setSlots([]);
    try {
      const dateStr = format(d, "yyyy-MM-dd");
      const res = await api.get(`/availability`, { params: { date: dateStr } });
      if (!res.data.open) {
        setClosedReason(res.data.reason || "No availability on this date.");
      } else {
        setSlots(res.data.slots);
      }
    } catch (err) {
      toast.error("Could not load availability. Please try again.");
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const handleDateSelect = (d) => {
    setDate(d);
    setSelectedTime(null);
    if (d) fetchSlots(d);
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const res = await api.post(`/manage/${token}/cancel`);
      setAppointment(res.data.appointment);
      setJustCanceled(true);
      setCancelOpen(false);
      toast.success("Your appointment has been canceled.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel. Please call us at " + COMPANY.phone);
    } finally {
      setCanceling(false);
    }
  };

  const handleReschedule = async () => {
    if (!date || !selectedTime) {
      toast.error("Please pick a new date and time.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/manage/${token}/reschedule`, {
        date: format(date, "yyyy-MM-dd"),
        time: selectedTime,
      });
      setAppointment(res.data.appointment);
      setJustRescheduled(true);
      setReschedOpen(false);
      setDate(null);
      setSelectedTime(null);
      setSlots([]);
      toast.success("Appointment rescheduled! A confirmation email is on its way.");
    } catch (e) {
      const detail = e.response?.data?.detail || "Reschedule failed. Please try again.";
      toast.error(detail);
      if (e.response?.status === 409 && date) {
        fetchSlots(date);
        setSelectedTime(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const disabledDays = [{ before: new Date() }, { dayOfWeek: [0, 6] }];

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-background">
      <header className="bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="manage-logo-link">
            <img src="/logo-white.png" alt="Cayman Fingerprinting Services Ltd." className="h-10 w-auto" />
            <span className="hidden sm:block text-[11px] text-white/50 border-l border-white/20 pl-3 uppercase tracking-wide">Manage Appointment</span>
          </Link>
          <Link to="/" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 transition-colors duration-200" data-testid="manage-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to website
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">{children}</main>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading your appointment...
        </div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <Card className="border bg-white" data-testid="manage-not-found">
          <CardContent className="p-10 text-center">
            <SearchX className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h1 className="text-xl font-heading font-semibold text-[color:var(--brand-navy)]">Appointment Not Found</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              This link appears to be invalid or expired. Please check the link in your confirmation email,
              or contact us and we'll be happy to help.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift">
                <Link to="/">Book a New Appointment</Link>
              </Button>
              <Button variant="outline" asChild className="btn-lift">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  const a = appointment;

  // Canceled state
  if (a.status === "canceled") {
    return (
      <Shell>
        <Card className="border bg-white" data-testid="manage-canceled-view">
          <CardContent className="p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-semibold text-[color:var(--brand-navy)]">
                  {justCanceled ? "Appointment Canceled" : "This Appointment Was Canceled"}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {justCanceled
                    ? "Your slot has been freed. A cancellation email has been sent to you."
                    : "This booking is no longer active."}
                </p>
              </div>
            </div>
            <div className="bg-secondary rounded-xl p-5 text-sm space-y-1.5">
              <p><span className="text-muted-foreground">Confirmation #:</span> <span className="font-mono-slot font-semibold text-[color:var(--brand-navy)]">{a.confirmation_number}</span></p>
              <p><span className="text-muted-foreground">Service:</span> <span className="font-medium text-[color:var(--brand-navy)]">{a.service}</span></p>
              <p><span className="text-muted-foreground">Was scheduled for:</span> <span className="font-medium text-[color:var(--brand-navy)]">{a.date_display} at {a.time_display}</span></p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift" data-testid="manage-book-new-button">
                <Link to="/"><CalendarPlus className="h-4 w-4 mr-2" /> Book a New Appointment</Link>
              </Button>
              <Button variant="outline" asChild className="btn-lift">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Past appointment
  if (a.is_past) {
    return (
      <Shell>
        <Card className="border bg-white" data-testid="manage-past-view">
          <CardContent className="p-8 sm:p-10 text-center">
            <Clock className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h1 className="text-xl font-heading font-semibold text-[color:var(--brand-navy)]">This Appointment Has Passed</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {a.service} on {a.date_display} at {a.time_display}. We hope we served you well!
            </p>
            <Button asChild className="mt-6 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift">
              <Link to="/">Book Another Appointment</Link>
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Active appointment - main manage view
  return (
    <Shell>
      <Card className="border bg-white shadow-sm" data-testid="manage-appointment-view">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${justRescheduled ? "bg-green-100" : "bg-secondary"}`}>
                {justRescheduled ? <CheckCircle2 className="h-6 w-6 text-green-600" /> : <CalendarCheck className="h-6 w-6 text-[color:var(--brand-navy)]" />}
              </div>
              <div>
                <h1 className="text-xl font-heading font-semibold text-[color:var(--brand-navy)]">
                  {justRescheduled ? "You're Rescheduled!" : `Hi ${a.name.split(" ")[0]}, here's your appointment`}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {justRescheduled ? "An updated confirmation email is on its way." : "You can reschedule or cancel below."}
                </p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Booked</Badge>
          </div>

          <div className="mt-6 bg-secondary rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Confirmation Number</span>
              <span className="font-mono-slot font-semibold text-lg text-[color:var(--brand-navy)]" data-testid="manage-confirmation-number">{a.confirmation_number}</span>
            </div>
            <Separator />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Service</p>
                <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{a.service}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Date &amp; Time</p>
                <p className="font-medium text-[color:var(--brand-navy)] mt-0.5" data-testid="manage-datetime">{a.date_display} at {a.time_display}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Location</p>
                <p className="font-medium text-[color:var(--brand-navy)] mt-0.5">{COMPANY.address}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] btn-lift"
              onClick={() => { setReschedOpen(!reschedOpen); }}
              data-testid="manage-reschedule-button"
            >
              <CalendarClock className="h-4 w-4 mr-2" /> {reschedOpen ? "Hide Reschedule" : "Reschedule"}
            </Button>
            <Button
              variant="outline"
              className="text-destructive border-destructive/40 hover:bg-red-50 hover:text-destructive btn-lift"
              onClick={() => setCancelOpen(true)}
              data-testid="manage-cancel-button"
            >
              <XCircle className="h-4 w-4 mr-2" /> Cancel Appointment
            </Button>
            <Button variant="outline" asChild className="btn-lift sm:ml-auto">
              <a href={a.google_calendar_link} target="_blank" rel="noopener noreferrer" data-testid="manage-google-calendar-button">
                <CalendarPlus className="h-4 w-4 mr-2" /> Google Calendar
              </a>
            </Button>
            <Button variant="outline" asChild className="btn-lift">
              <a href={`${API_BASE}/appointments/${a.id}/calendar.ics`} download data-testid="manage-ics-button">
                .ics
              </a>
            </Button>
          </div>

          {reschedOpen && (
            <div className="mt-8 border rounded-xl p-5" data-testid="manage-reschedule-panel">
              <h2 className="font-heading font-semibold text-[color:var(--brand-navy)] mb-4">Pick a New Date &amp; Time</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleDateSelect}
                    disabled={disabledDays}
                    className="rounded-xl border w-fit mx-auto md:mx-0"
                    data-testid="manage-calendar"
                  />
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> Open Monday&ndash;Friday, 9 AM &ndash; 3 PM.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[color:var(--brand-navy)] mb-3">
                    {date ? `Available times for ${format(date, "EEEE, MMMM d")}` : "Select a date to see available times"}
                  </p>
                  {slotsLoading ? (
                    <div className="flex items-center justify-center h-36 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading availability...
                    </div>
                  ) : closedReason ? (
                    <div className="p-4 bg-secondary rounded-lg text-sm text-muted-foreground">{closedReason}</div>
                  ) : slots.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {slots.map((slot) => {
                        const isSelected = selectedTime === slot.time;
                        const isCurrent = a.date === format(date, "yyyy-MM-dd") && a.time === slot.time;
                        const disabled = (!slot.available && !isCurrent) || isCurrent;
                        return (
                          <button
                            key={slot.time}
                            disabled={disabled}
                            onClick={() => setSelectedTime(slot.time)}
                            className={`slot-button relative rounded-lg border p-2.5 min-h-[52px] text-sm font-mono-slot ${
                              disabled
                                ? "opacity-45 cursor-not-allowed bg-muted border-border"
                                : isSelected
                                ? "border-[color:var(--brand-sky)] bg-blue-50/60 ring-2 ring-[hsl(var(--ring))] font-semibold"
                                : "bg-white border-border hover:bg-[color:var(--brand-sand)]"
                            }`}
                            data-testid={`manage-time-slot-${slot.time.replace(":", "")}`}
                            title={isCurrent ? "Your current time" : disabled ? "Unavailable" : `${slot.remaining} of 3 spots left`}
                          >
                            <span className="block text-[color:var(--brand-navy)]">{slot.time_display}</span>
                            <Badge variant="secondary" className="mt-1 text-[10px] px-1.5 py-0">
                              {isCurrent ? "Current" : slot.past ? "Passed" : slot.remaining === 0 ? "Full" : `${slot.remaining}/3 left`}
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-36 border border-dashed rounded-lg text-sm text-muted-foreground">
                      <CalendarCheck className="h-4 w-4 mr-2" /> Pick a weekday on the calendar
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <Button
                  className="bg-[color:var(--brand-sky)] hover:bg-[#1791c5] text-white font-semibold btn-lift"
                  disabled={!date || !selectedTime || saving}
                  onClick={handleReschedule}
                  data-testid="manage-confirm-reschedule-button"
                >
                  {saving ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Rescheduling...</span>
                  ) : (
                    <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Confirm New Time</span>
                  )}
                </Button>
              </div>
            </div>
          )}

          <p className="mt-6 text-xs text-muted-foreground">
            Questions? Call us at <a href={COMPANY.phoneHref} className="text-[color:var(--brand-sky)] hover:underline">{COMPANY.phone}</a> or
            email <a href={COMPANY.emailHref} className="text-[color:var(--brand-sky)] hover:underline">{COMPANY.email}</a>.
          </p>
        </CardContent>
      </Card>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="manage-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {a.service} on {a.date_display} at {a.time_display}. Your slot will be released.
              You can always book a new appointment later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manage-cancel-dialog-dismiss">Keep My Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancel(); }}
              className="bg-destructive hover:bg-destructive/90"
              disabled={canceling}
              data-testid="manage-cancel-dialog-confirm"
            >
              {canceling ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Yes, Cancel It
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
